import { type ComponentType, lazy, type LazyExoticComponent } from 'react';
import { type FileTypeGroup, fileTypeGroupOf } from '@apm/contracts';
import {
  OFFICE_PREVIEW_MAX_BYTES,
  SHEET_PREVIEW_MAX_BYTES,
  TEXT_PREVIEW_MAX_BYTES,
} from './file-content';
import { type ZoomKind } from './viewer-controls';

/**
 * Chữ ký chung của mọi trình xem — plan §4.3. Trình xem chỉ biết URL đã ký,
 * tên và dung lượng; tải về, chuyển file, đóng là việc của khung.
 *
 * Trình xem **ném** khi hỏng (lỗi đọc, lỗi thư viện, `<video>` báo `error`):
 * ranh giới lỗi của khung bắt và hiện màn hình tải về kèm lý do. Không trình
 * xem nào tự vẽ màn hình lỗi riêng.
 */
export interface ViewerProps {
  readonly url: string;
  readonly name: string;
  readonly sizeBytes: number | null;
}

interface ViewerMatch {
  /** Tên trình xem — cho test và `data-viewer` trên DOM. */
  readonly kind: string;
  readonly matches: (file: {
    readonly extension: string;
    readonly group: FileTypeGroup;
  }) => boolean;
}

export interface PreviewableViewer extends ViewerMatch {
  /** Trần dung lượng; `null` = không trần (video/âm thanh tải theo đoạn). */
  readonly maxBytes: number | null;
  readonly component: LazyExoticComponent<ComponentType<ViewerProps>>;
  /**
   * Trình duyệt này chạy được thư viện không — kiểm **trước** khi nạp chunk.
   * `false` ⇒ khung hiện màn hình tải về với lỗi `browser`. Bỏ trống = luôn được.
   */
  readonly browserSupported?: () => boolean;
  /**
   * Kiểu zoom của thanh công cụ (APM-PVW-a): `scale` cho thứ có khổ cố định
   * (phóng hình), `reflow` cho chữ (tăng cỡ chữ, dàn lại dòng). Bỏ trống = không
   * có zoom (bảng tính, video, HTML trong iframe…). Xem `viewer-controls.tsx`.
   */
  readonly zoom?: ZoomKind;
  /**
   * `'dom'` = tìm theo từ khoá ngay trên DOM trình xem dựng ra (APM-PVW-d) —
   * thanh công cụ tự làm, trình xem không phải khai gì. Thứ không có chữ trong DOM
   * (PDF vẽ canvas) tự khai `search` lúc chạy qua `useRegisterViewer`.
   */
  readonly search?: 'dom';
  readonly downloadOnly?: never;
}

/**
 * Lý do một định dạng **nhận ra được** nhưng chỉ tải về — khoá
 * `myFiles.preview.downloadOnly.<lý do>`. Khác với "không nhận ra" (`null`):
 * người dùng được nói rõ vì sao, và cách tự xoay (APM-MYF-e, `.ppt`).
 */
export type DownloadOnlyReason = 'ppt';

export interface DownloadOnlyViewer extends ViewerMatch {
  readonly downloadOnly: DownloadOnlyReason;
}

export type ViewerEntry = PreviewableViewer | DownloadOnlyViewer;

const extensionIn =
  (...extensions: readonly string[]) =>
  ({ extension }: { readonly extension: string }): boolean =>
    extensions.includes(extension);

const groupIs =
  (group: FileTypeGroup) =>
  (file: { readonly group: FileTypeGroup }): boolean =>
    file.group === group;

/**
 * pdf.js 6 dùng `AbortSignal.any` (Chrome 116+, Safari/iOS 17.4+; bản 6 đặt mốc
 * hỗ trợ ở Chrome 125 / iOS 18). Thiếu nó thì thư viện hỏng giữa chừng, nên
 * kiểm **trước** khi nạp chunk: khung hiện màn hình tải về có lý do `browser`,
 * không phải màn hình trắng.
 */
export function supportsPdfJs(): boolean {
  return typeof AbortSignal !== 'undefined' && typeof AbortSignal.any === 'function';
}

/**
 * Bảng trình xem, **thứ tự là ưu tiên** (khớp đầu tiên thắng): html và md đứng
 * trước nhóm `code`/`text` chứa chúng. Thêm trình xem là thêm **một** mục ở đây.
 *
 * Mọi trình xem nạp lười bằng `import()` — Shiki, papaparse, pdf.js, SheetJS…
 * không bao giờ vào bundle chính (plan §2.6).
 *
 * Office nhận theo **đuôi**, không theo nhóm: nhóm `document` gộp docx, doc,
 * odt, rtf — mỗi thứ một thư viện hoặc không thư viện nào.
 */
export const VIEWERS: readonly ViewerEntry[] = [
  {
    kind: 'html',
    matches: extensionIn('html', 'htm'),
    maxBytes: TEXT_PREVIEW_MAX_BYTES,
    component: lazy(() => import('./viewers/html-viewer')),
  },
  {
    // Trước nhóm `document` (APM-CD-a): `.chilldoc` là JSON của editor, không phải Office.
    kind: 'chilldoc',
    search: 'dom',
    zoom: 'reflow',
    matches: extensionIn('chilldoc'),
    maxBytes: TEXT_PREVIEW_MAX_BYTES,
    component: lazy(() => import('./viewers/chilldoc-viewer')),
  },
  {
    kind: 'markdown',
    search: 'dom',
    zoom: 'reflow',
    matches: extensionIn('md', 'markdown'),
    maxBytes: TEXT_PREVIEW_MAX_BYTES,
    component: lazy(() => import('./viewers/markdown-viewer')),
  },
  {
    kind: 'csv',
    matches: extensionIn('csv', 'tsv'),
    maxBytes: SHEET_PREVIEW_MAX_BYTES,
    component: lazy(() => import('./viewers/csv-viewer')),
  },
  {
    kind: 'pdf',
    zoom: 'scale',
    matches: groupIs('pdf'),
    maxBytes: OFFICE_PREVIEW_MAX_BYTES,
    browserSupported: supportsPdfJs,
    component: lazy(() => import('./viewers/pdf-viewer')),
  },
  {
    kind: 'docx',
    search: 'dom',
    zoom: 'scale',
    matches: extensionIn('docx'),
    maxBytes: OFFICE_PREVIEW_MAX_BYTES,
    component: lazy(() => import('./viewers/docx-viewer')),
  },
  {
    kind: 'doc',
    matches: extensionIn('doc'),
    maxBytes: OFFICE_PREVIEW_MAX_BYTES,
    component: lazy(() => import('./viewers/doc-viewer')),
  },
  {
    kind: 'sheet',
    zoom: 'scale',
    matches: extensionIn('xlsx', 'xls'),
    maxBytes: OFFICE_PREVIEW_MAX_BYTES,
    component: lazy(() => import('./viewers/sheet-viewer')),
  },
  {
    kind: 'pptx',
    zoom: 'scale',
    matches: extensionIn('pptx'),
    maxBytes: OFFICE_PREVIEW_MAX_BYTES,
    component: lazy(() => import('./viewers/pptx-viewer')),
  },
  // PowerPoint 97–2003: không có thư viện mã nguồn mở dùng được (plan §2.6).
  { kind: 'ppt', matches: extensionIn('ppt'), downloadOnly: 'ppt' },
  {
    kind: 'code',
    search: 'dom',
    zoom: 'reflow',
    matches: groupIs('code'),
    maxBytes: TEXT_PREVIEW_MAX_BYTES,
    component: lazy(() => import('./viewers/code-viewer')),
  },
  {
    kind: 'text',
    search: 'dom',
    zoom: 'reflow',
    matches: groupIs('text'),
    maxBytes: TEXT_PREVIEW_MAX_BYTES,
    component: lazy(() => import('./viewers/text-viewer')),
  },
  {
    kind: 'image',
    zoom: 'scale',
    matches: groupIs('image'),
    maxBytes: null,
    component: lazy(() =>
      import('./viewers/media-viewer').then((module) => ({ default: module.ImageViewer })),
    ),
  },
  {
    kind: 'video',
    matches: groupIs('video'),
    maxBytes: null,
    component: lazy(() =>
      import('./viewers/media-viewer').then((module) => ({ default: module.VideoViewer })),
    ),
  },
  {
    kind: 'audio',
    matches: groupIs('audio'),
    maxBytes: null,
    component: lazy(() =>
      import('./viewers/media-viewer').then((module) => ({ default: module.AudioViewer })),
    ),
  },
];

/** Đuôi viết thường, `''` khi không có (kể cả `.gitignore`) — cùng luật với `fileTypeGroupOf`. */
export function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 1 ? name.slice(dot + 1).toLowerCase() : '';
}

/**
 * Trình xem cho một file — theo đuôi trước, `mimeType` sau (plan §2.6).
 * `null` ⇒ chưa xem trước được, khung hiện biểu tượng lớn + tải về.
 */
export function viewerFor(name: string, mimeType: string | null): ViewerEntry | null {
  const file = { extension: extensionOf(name), group: fileTypeGroupOf(name, mimeType) };
  return VIEWERS.find((entry) => entry.matches(file)) ?? null;
}
